import { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { PatientContext } from "../../../../context/PatientContext/PatientContext";
import { CarePlanContext } from "../../../../context/CarePlanContext/CarePlanContext";
import CareGoalCard from "./cards/CareGoalCard";
import { useTranslation } from "react-i18next";
import CarePlanSuccessSummary from "./cards/CarePlanSuccessSummary";
import { Button } from "@mui/material";
import secureStorage from "../../../../utils/security/secureStorage";

export default function CarePlanOverview() {

  const { t } = useTranslation();
  const { patientDetails, fetchPatientDetails } = useContext(PatientContext);
  const {
    fetchCareGoalsByPlanId,
    fetchTreatmentsByPlanId,
    fetchFollowUpsByPlanId,
  } = useContext(CarePlanContext);

  const [viewSummary, setViewSummary] = useState(false);
  const [careGoals, setCareGoals] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [followUps, setFollowUps] = useState([]);

  const patient = Array.isArray(patientDetails) ? patientDetails[0] : null;

  // Obtener detalle del paciente desde secureStorage (HIPAA: IDs de paciente
  // encriptados en sessionStorage, no localStorage plano)
  useEffect(() => {
    const cipId = secureStorage.getItem("selectedCIPId");
    if (cipId) {
      fetchPatientDetails(cipId);
    }
  }, [fetchPatientDetails]);

  useEffect(() => {
    const carePlanId = secureStorage.getItem("currentCarePlanId");

    // HIPAA: no loguear care goals/treatments/follow-ups (contenido clínico)
    if (carePlanId) {
      fetchCareGoalsByPlanId(carePlanId).then(setCareGoals);
      fetchTreatmentsByPlanId(carePlanId).then(setTreatments);
      fetchFollowUpsByPlanId(carePlanId).then(setFollowUps);
    }
  }, [fetchCareGoalsByPlanId, fetchTreatmentsByPlanId, fetchFollowUpsByPlanId]);


  return (
    <Box sx={{ width: "100%", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Título */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          mb: 2,
          textTransform: "uppercase",
          color: "#085394",
        }}
      >
        Care Plan
      </Typography>

      {/* Información del paciente */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: 2,
          p: 2,
          display: "flex",
          flexDirection: "row",
          gap: 4,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ color: "#085394", fontWeight: "bold", mb: 1 }}>
            {t('CarePlan.CarePlanTemplate.PatientInformation')}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.MemberFullName')}: {patient?.memberFullName || "Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.DOB')}: {patient?.dateOfBirth?.split("T")[0] || "Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.MemberPhone')}: {patient?.memberPhone || "Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.CityName')}: {"Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.CareManager')}: {"Unassigned"}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ color: "#085394", fontWeight: "bold", mb: 1 }}>
            {t('CarePlan.CarePlanTemplate.BasicInformation')}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.MemberID')}: {patient?.varCipId || "Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.Gender')}: {patient?.gender || "Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.Address')}: {"Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.OfficeLocation')}: {"Unassigned"}
          </Typography>
          <Typography variant="body1">
            {t('CarePlan.CarePlanTemplate.PCP')}: {"Unassigned"}
          </Typography>
        </Box>
      </Box>

            {!viewSummary && (
        <>
          {careGoals.length === 0 ? (
            <Typography variant="body1" sx={{ fontStyle: "italic", mt: 2 }}>
              No Care Goals yet.
            </Typography>
          ) : (
            careGoals.map((goal) => {
              const relatedTreatments = treatments.filter(
                (t) => t.careGoalId === goal.careGoalId
              );
              const relatedFollowUps = followUps.filter(
                (f) => f.careGoalId === goal.careGoalId
              );

              return (
                <CareGoalCard
                  key={goal.careGoalId}
                  caregoalId={goal.careGoalId}
                  name={goal.name}
                  description={goal.description}
                  treatments={relatedTreatments}
                  followUps={relatedFollowUps}
                />
              );
            })
          )}
        </>
      )}

      {!viewSummary && (
        <Box textAlign="center" mt={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setViewSummary(true)}
          >
            Save Care Plan
          </Button>
        </Box>
      )}

      {viewSummary && (
        <CarePlanSuccessSummary
          careGoals={careGoals.map((goal) => ({
            caregoalId: goal.careGoalId,
            name: goal.name,
            description: goal.description,
            treatments: treatments.filter((t) => t.careGoalId === goal.careGoalId),
            followUps: followUps.filter((f) => f.careGoalId === goal.careGoalId),
          }))}
          onReturn={() => setViewSummary(false)}
          patientInfo = {patient}
        />
      )}

    </Box>
  );
}
